import axiosInstance from "./axiosInstance";
import logger from "../utils/logger";
import {
    Session,
    UpdateSessionDto,
    AdminMessageDto
} from "../models/session";
import { Message } from "../models/message";
import { normalizeSessionHttpSnapshot } from "../services/sessionAuthority";

const CHAT_API_BASE_PATH = "/sessions"; // Backend routes are prefixed with /sessions

const safeErrorDetails = (error: unknown): { errorType: string; status?: number } => {
    const errorType = error instanceof Error ? error.name : "UnknownError";
    if (typeof error !== "object" || error === null || !("response" in error)) {
        return { errorType };
    }
    const response = (error as { response?: { status?: unknown } }).response;
    return typeof response?.status === "number"
        ? { errorType, status: response.status }
        : { errorType };
};

const isRequestCancellation = (error: unknown): boolean =>
    error instanceof Error && ['AbortError', 'CanceledError'].includes(error.name);

// GET /sessions
export const getSessions = async (signal?: AbortSignal): Promise<Session[]> => {
    try {
        logger.debug("Fetching all chat sessions");
        const response = await axiosInstance.get<unknown>(CHAT_API_BASE_PATH, { signal });
        const sessions = Array.isArray(response.data)
            ? response.data.map(normalizeSessionHttpSnapshot)
            : response.data;
        logger.debug(`Successfully fetched ${Array.isArray(sessions) ? sessions.length : 0} sessions`);
        return sessions as Session[];
    } catch (error: unknown) {
        if (!isRequestCancellation(error)) {
            logger.error("Fetching chat sessions failed", safeErrorDetails(error));
        }
        throw error;
    }
};

// GET /sessions/{workerId}/messages
export const getMessages = async (workerId: string): Promise<Message[]> => {
    try {
        logger.debug("Fetching session messages");
        const response = await axiosInstance.get<Message[]>(`${CHAT_API_BASE_PATH}/${workerId}/messages`, { params: { limit: 100 } });
        logger.debug(`Successfully fetched ${response.data.length} session messages`);
        return response.data;
    } catch (error: unknown) {
        logger.error("Fetching session messages failed", safeErrorDetails(error));
        throw error;
    }
};

// POST /sessions/{workerId}/pause-ai
export const pauseAi = async (workerId: string): Promise<Session> => {
    try {
        logger.debug("Pausing AI for session");
        const response = await axiosInstance.post<Session>(`${CHAT_API_BASE_PATH}/${workerId}/pause-ai`);
        logger.debug("Successfully paused AI for session");
        return response.data; // Assuming backend returns the updated session
    } catch (error: unknown) {
        logger.error("Pausing AI for session failed", safeErrorDetails(error));
        throw error;
    }
};

// POST /sessions/{workerId}/resume-ai
export const resumeAi = async (workerId: string): Promise<Session> => {
    try {
        logger.debug("Resuming AI for session");
        const response = await axiosInstance.post<Session>(`${CHAT_API_BASE_PATH}/${workerId}/resume-ai`);
        logger.debug("Successfully resumed AI for session");
        return response.data; // Assuming backend returns the updated session
    } catch (error: unknown) {
        logger.error("Resuming AI for session failed", safeErrorDetails(error));
        throw error;
    }
};

// POST /sessions/{workerId}/admin-message
export const sendAdminMessage = async (workerId: string, messageDto: AdminMessageDto): Promise<{ message?: Message; duplicate: boolean }> => {
    // Assuming the backend endpoint for sending an admin message is: POST /sessions/{workerId}/admin-message
    // And it expects AdminMessageDto in the body.
    // The return type is void if the backend sends a 200/201/204 without a body for this operation.
    // If it returns the created message, change Promise<void> to Promise<Message> and return response.data.
    try {
        logger.debug("Sending admin message");
        const response = await axiosInstance.post<{ message?: Message; duplicate?: boolean }>(`${CHAT_API_BASE_PATH}/${workerId}/admin-message`, messageDto, {
            headers: { 'Idempotency-Key': messageDto.clientRequestId },
        });
        logger.debug("Successfully sent admin message");
        return { message: response.data?.message, duplicate: response.data?.duplicate === true };
    } catch (error: unknown) {
        logger.error("Sending admin message failed", safeErrorDetails(error));
        throw error;
    }
};

// POST /sessions/{workerId}/mark-viewed
export const markSessionAsViewed = async (workerId: string): Promise<Session> => {
    try {
        logger.debug("Marking session as viewed");
        const response = await axiosInstance.post<Session>(`${CHAT_API_BASE_PATH}/${workerId}/mark-viewed`);
        logger.debug("Successfully marked session as viewed");
        return response.data; // Backend returns the updated session
    } catch (error: unknown) {
        logger.error("Marking session as viewed failed", safeErrorDetails(error));
        throw error;
    }
};

// PATCH /sessions/{workerId} (Example, if needed for other session updates)
export const updateSession = async (workerId: string, updates: UpdateSessionDto): Promise<Session> => {
    try {
        logger.debug("Updating session");
        const response = await axiosInstance.patch<Session>(`${CHAT_API_BASE_PATH}/${workerId}`, updates);
        logger.debug("Successfully updated session");
        return response.data;
    } catch (error: unknown) {
        logger.error("Updating session failed", safeErrorDetails(error));
        throw error;
    }
};

export const getSessionByWorkerId = async (
    workerId: string,
    signal?: AbortSignal,
): Promise<Session | null> => {
    try {
        const response = await axiosInstance.get<Session>(`/sessions/worker/${workerId}`, { signal });
        return response.data;
    } catch (error: unknown) {
        if (isRequestCancellation(error)) throw error;
        const details = safeErrorDetails(error);
        if (details.status === 404) {
            logger.warn("No session found for requested worker");
            return null;
        }
        logger.error("Fetching session details failed", details);
        throw new Error("Failed to fetch session details.");
    }
};

export interface SuggestedAttribution {
    referralLinkId: string;
    referralLinkTitle: string;
    recruiterName?: string | null;
    evidence: string;
    score: 'low' | 'medium' | 'high';
}

// GET /sessions/worker/{workerId}/suggested-attribution
export const getSuggestedAttribution = async (workerId: string): Promise<SuggestedAttribution[]> => {
    try {
        logger.debug("Fetching attribution suggestions");
        const response = await axiosInstance.get<SuggestedAttribution[]>(
            `${CHAT_API_BASE_PATH}/worker/${workerId}/suggested-attribution`
        );
        return response.data;
    } catch (error: unknown) {
        logger.error("Fetching attribution suggestions failed", safeErrorDetails(error));
        throw error;
    }
};

// POST /sessions/worker/{workerId}/manual-attribution
export const setManualAttribution = async (workerId: string, referralLinkId: string): Promise<Session> => {
    try {
        logger.debug("Setting manual attribution");
        const response = await axiosInstance.post<Session>(
            `${CHAT_API_BASE_PATH}/worker/${workerId}/manual-attribution`,
            { referralLinkId }
        );
        return response.data;
    } catch (error: unknown) {
        logger.error("Setting manual attribution failed", safeErrorDetails(error));
        throw error;
    }
};

// POST /sessions/messages/{messageId}/translate
export const translateMessage = async (messageId: string, targetLanguage: string = 'en'): Promise<Message> => {
    try {
        logger.debug("Translating message");
        const response = await axiosInstance.post<Message>(
            `${CHAT_API_BASE_PATH}/messages/${messageId}/translate`,
            { targetLanguage }
        );
        logger.debug("Successfully translated message");
        return response.data;
    } catch (error: unknown) {
        logger.error("Translating message failed", safeErrorDetails(error));
        throw error;
    }
};
